import type { IndexerRelease } from '@mcgames/contracts';
import { Badge, Button, Select, Spinner, TextInput } from '@mcgames/ui';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatDate, formatDateTime } from '../../lib/format-date.ts';
import { useFormatBytes } from '../../lib/format-bytes.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { ErrorText } from '../games/ErrorText.tsx';
import {
  PAGE_SIZE,
  useIndexerCategories,
  useIndexerSearch,
  useIndexerStatus,
  type IndexerSearchParams,
} from './indexer-hooks.ts';
import { isOldRelease, releaseAgeYears } from './release-age.ts';
import styles from './Indexer.module.css';

export type IndexerSearchProps = {
  initialQuery?: string;
  /** Label of the button in every row. */
  actionLabel: string;
  /** Accessible name of a row's button, to tell the identical buttons apart. */
  actionAriaLabel?: (release: IndexerRelease) => string;
  onPick: (release: IndexerRelease) => void;
  /** The release whose action is running; its button is disabled. */
  pendingGuid?: string | null;
  /** True for a release whose import is running; its button is disabled. */
  isBusy?: (release: IndexerRelease) => boolean;
  /** Extra content under the release title, for example the state of an import. */
  rowStatus?: (release: IndexerRelease) => ReactNode;
};

/**
 * Search form and result table of the indexer; the caller decides what picking a release does. A
 * release a game already uses, or one posted more than `OLD_RELEASE_YEARS` years ago, is only
 * picked after the admin confirms: it would be grabbed again, or its articles are likely missing.
 * A release that is both gets one dialog that names both reasons.
 */
export function IndexerSearch({
  initialQuery = '',
  actionLabel,
  actionAriaLabel,
  onPick,
  pendingGuid = null,
  isBusy,
  rowStatus,
}: IndexerSearchProps) {
  const { t, i18n } = useTranslation('admin');
  const formatBytes = useFormatBytes();
  const status = useIndexerStatus();
  const categories = useIndexerCategories();
  const [text, setText] = useState(initialQuery);
  const [confirming, setConfirming] = useState<IndexerRelease | null>(null);
  // `null` follows the configured default until the admin picks a category.
  const [chosenCat, setChosenCat] = useState<string | null>(null);
  // `cat: null` follows the configured default until the admin searches with a category.
  const [submitted, setSubmitted] = useState<{ q: string; cat: string | null; offset: number }>({
    q: initialQuery.trim(),
    cat: null,
    offset: 0,
  });

  const defaultCat =
    status.data?.defaultCategories.length === 1 ? status.data.defaultCategories[0] : undefined;
  const options = [
    { value: '', label: t('indexer.search.defaultCategories') },
    ...(categories.data ?? []).flatMap((category) => [
      { value: category.id, label: category.name },
      ...category.subcategories.map((sub) => ({
        value: sub.id,
        label: `${category.name} / ${sub.name}`,
      })),
    ]),
  ];
  const wanted = chosenCat ?? defaultCat ?? '';
  const cat = options.some((option) => option.value === wanted) ? wanted : '';

  // The first search waits for the status so that it already uses the default category.
  const effective: IndexerSearchParams = {
    q: submitted.q,
    cat: submitted.cat ?? defaultCat ?? '',
    offset: submitted.offset,
  };
  const search = useIndexerSearch(effective, status.data !== undefined);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted({ q: text.trim(), cat, offset: 0 });
  }

  function pick(release: IndexerRelease) {
    if (release.importedGameId !== null || isOldRelease(release.postedAt)) setConfirming(release);
    else onPick(release);
  }

  function confirmText(release: IndexerRelease) {
    const { postedAt } = release;
    const used = release.importedGameId !== null;
    const old = postedAt !== null && isOldRelease(postedAt);
    const parts = [
      used ? t('indexer.again.message', { title: release.title }) : null,
      old
        ? t('indexer.old.message', {
            date: formatDate(postedAt, i18n.language),
            count: releaseAgeYears(postedAt),
          })
        : null,
    ];
    return {
      title: used ? t('indexer.again.title') : t('indexer.old.title'),
      message: parts.filter((part) => part !== null).join(' '),
      confirmLabel: old ? t('indexer.old.confirm') : actionLabel,
    };
  }

  const total = search.data?.total ?? 0;
  const releases = search.data?.releases ?? [];
  const from = effective.offset + 1;
  const to = effective.offset + releases.length;

  return (
    <div className={styles['section']}>
      <form onSubmit={submit}>
        <div className={styles['row']}>
          <div className={styles['grow']}>
            <TextInput
              label={t('indexer.search.query')}
              placeholder={t('indexer.search.placeholder')}
              value={text}
              maxLength={200}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
          <div className={styles['fieldWidth']}>
            <Select
              label={t('indexer.search.category')}
              value={cat}
              options={options}
              onChange={setChosenCat}
            />
          </div>
          <Button type="submit">{t('indexer.search.submit')}</Button>
        </div>
      </form>
      {(status.isPending || search.isPending) && !search.isError && !status.isError && <Spinner />}
      <ErrorText error={status.error ?? search.error} />
      {search.data !== undefined && releases.length === 0 && (
        <p className={styles['muted']}>{t('indexer.search.empty')}</p>
      )}
      {releases.length > 0 && (
        <>
          <div className={styles['tableWrap']}>
            <table className={styles['table']} aria-label={t('indexer.search.label')}>
              <thead>
                <tr>
                  <th scope="col">{t('indexer.columns.release')}</th>
                  <th scope="col">{t('indexer.columns.size')}</th>
                  <th scope="col">{t('indexer.columns.posted')}</th>
                  <th scope="col">{t('indexer.columns.category')}</th>
                  <th scope="col">{t('indexer.columns.action')}</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.guid}>
                    <td className={styles['wrap']}>
                      <div className={styles['releaseCell']}>
                        <span className={styles['releaseTitle']}>{release.title}</span>
                        {release.importedGameId !== null && (
                          <Link
                            to={`/admin/games/${encodeURIComponent(release.importedGameId)}`}
                            className={styles['badgeLink']}
                          >
                            <Badge tone="accent">{t('indexer.imported')}</Badge>
                          </Link>
                        )}
                        {rowStatus?.(release)}
                      </div>
                    </td>
                    <td className={styles['nowrap']}>{formatBytes(release.size)}</td>
                    <td className={styles['nowrap']}>
                      {release.postedAt !== null ? (
                        <div className={styles['postedCell']}>
                          <span>{formatDateTime(release.postedAt, i18n.language)}</span>
                          {isOldRelease(release.postedAt) && (
                            <Badge tone="danger">
                              {t('indexer.old.badge', {
                                count: releaseAgeYears(release.postedAt),
                              })}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        t('indexer.unknown')
                      )}
                    </td>
                    <td className={styles['wrap']}>{release.category ?? t('indexer.unknown')}</td>
                    <td>
                      <Button
                        size="sm"
                        aria-label={actionAriaLabel?.(release)}
                        disabled={pendingGuid === release.guid || isBusy?.(release) === true}
                        onClick={() => pick(release)}
                      >
                        {actionLabel}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles['row']}>
            <Button
              variant="secondary"
              size="sm"
              disabled={effective.offset === 0}
              onClick={() =>
                setSubmitted({ ...submitted, offset: Math.max(0, effective.offset - PAGE_SIZE) })
              }
            >
              {t('indexer.search.previous')}
            </Button>
            <span className={styles['muted']}>{t('indexer.search.page', { from, to, total })}</span>
            <Button
              variant="secondary"
              size="sm"
              disabled={to >= total}
              onClick={() => setSubmitted({ ...submitted, offset: effective.offset + PAGE_SIZE })}
            >
              {t('indexer.search.next')}
            </Button>
          </div>
        </>
      )}
      {confirming !== null && (
        <ConfirmDialog
          {...confirmText(confirming)}
          tone="primary"
          pending={false}
          error={null}
          onConfirm={() => {
            setConfirming(null);
            onPick(confirming);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}
